

import React from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Github } from 'lucide-react';

interface FooterSection {
  title: string;
  links: string[];
}

const Footer: React.FC = () => {
  const footerSections: FooterSection[] = [
    {
      title: '产品',
      links: ['云超算标准版', '云超算企业版', '云超算专享版', 'ChainCloud Studio']
    },
    {
      title: '解决方案',
      links: ['生物医药', '汽车制造', '材料科学', '金融计算']
    },
    {
      title: '支持',
      links: ['技术文档', '帮助中心', '开发者指南', '社区论坛']
    },
    {
      title: '公司',
      links: ['关于我们', '新闻动态', '招聘信息', '联系我们']
    }
  ];

  const handleLinkClick = (link: string) => {
    console.log('导航到:', link);
  };

  return (
    <footer className="bg-gray-900 text-white relative">
      {/* Main Footer */}
      <div className="container-responsive py-8 lg:py-12 xl:py-16 2xl:py-20 portrait:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 xl:gap-12 portrait:gap-4">
          {/* Brand Section */}
          <div className="lg:col-span-5">
            <div className="mb-4 lg:mb-6 portrait:mb-3">
              <h3 className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl portrait:text-base font-bold gradient-text mb-2 lg:mb-3 xl:mb-4 portrait:mb-2">
                ChainCloud云超算
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm lg:text-base xl:text-lg portrait:text-xs max-w-md">
                全栈Cloud-HPC数字化研发平台，让高性能计算更简单、更高效。
                提供从底层硬件到上层应用的完整解决方案，助力科研与工业创新。
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-2 lg:space-y-3 portrait:space-y-1.5">
              <div className="flex items-center gap-2 lg:gap-3 portrait:gap-1.5 text-gray-400 text-sm lg:text-base portrait:text-xs">
                <Phone className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-3 portrait:h-3" />
                <span>400-123-4567</span>
              </div>
              <div className="flex items-center gap-2 lg:gap-3 portrait:gap-1.5 text-gray-400 text-sm lg:text-base portrait:text-xs">
                <Mail className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-3 portrait:h-3" />
                <span>contact@chaincloud.com</span>
              </div>
              <div className="flex items-center gap-2 lg:gap-3 portrait:gap-1.5 text-gray-400 text-sm lg:text-base portrait:text-xs">
                <MapPin className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-3 portrait:h-3" />
                <span>北京市朝阳区科技园区100号</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-2 lg:gap-3 xl:gap-4 portrait:gap-2 mt-4 lg:mt-6 portrait:mt-3">
              {[
                { icon: <Facebook className="w-4 h-4 lg:w-5 lg:h-5 portrait:w-3 portrait:h-3" />, name: 'Facebook' },
                { icon: <Twitter className="w-4 h-4 lg:w-5 lg:h-5 portrait:w-3 portrait:h-3" />, name: 'Twitter' },
                { icon: <Linkedin className="w-4 h-4 lg:w-5 lg:h-5 portrait:w-3 portrait:h-3" />, name: 'LinkedIn' },
                { icon: <Github className="w-4 h-4 lg:w-5 lg:h-5 portrait:w-3 portrait:h-3" />, name: 'GitHub' }
              ].map((social, index) => (
                <button
                  key={index}
                  className="w-7 h-7 lg:w-8 lg:h-8 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 portrait:w-6 portrait:h-6 bg-gray-800 rounded-lg portrait:rounded-md flex items-center justify-center text-gray-400 hover:text-white hover:bg-primary-600 transition-all duration-300"
                  title={social.name}
                >
                  {social.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6 xl:gap-8 portrait:gap-3">
              {footerSections.map((section, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-white mb-2 lg:mb-3 xl:mb-4 portrait:mb-2 text-sm lg:text-base xl:text-lg portrait:text-xs">
                    {section.title}
                  </h4>
                  <div className="space-y-1.5 lg:space-y-2 xl:space-y-3 portrait:space-y-1">
                    {section.links.map((link, linkIndex) => (
                      <button
                        key={linkIndex}
                        onClick={() => handleLinkClick(link)}
                        className="block text-gray-400 hover:text-white transition-colors duration-300 text-xs lg:text-sm xl:text-base portrait:text-xs text-left"
                      >
                        {link}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-t border-gray-800">
        <div className="container-responsive py-4 lg:py-6 xl:py-8 portrait:py-3">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-4 xl:gap-6 portrait:gap-2">
            <div className="w-full lg:w-auto">
              <h4 className="font-semibold text-white mb-1 lg:mb-2 portrait:mb-1 text-sm lg:text-base xl:text-lg portrait:text-xs">
                订阅我们的资讯
              </h4>
              <p className="text-gray-400 text-xs lg:text-sm xl:text-base portrait:text-xs">
                获取最新的产品更新和技术资讯
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 portrait:gap-1.5 w-full lg:w-auto">
              <input
                type="email"
                placeholder="输入您的邮箱地址"
                className="flex-1 sm:w-48 lg:w-64 xl:w-80 portrait:w-full px-3 lg:px-4 portrait:px-2 py-2 lg:py-3 portrait:py-1.5 bg-gray-800 border border-gray-700 rounded-lg portrait:rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors duration-300 text-sm lg:text-base portrait:text-xs"
              />
              <button className="px-3 lg:px-4 xl:px-6 portrait:px-2 py-2 lg:py-3 portrait:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg portrait:rounded-md font-medium hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap text-sm lg:text-base portrait:text-xs">
                订阅
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-responsive py-3 lg:py-4 xl:py-6 portrait:py-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 lg:gap-3 xl:gap-4 portrait:gap-1.5">
            <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 portrait:gap-2 text-xs lg:text-sm xl:text-base portrait:text-xs text-gray-400">
              <span>© 2024 ChainCloud云超算. 保留所有权利.</span>
            </div>
            <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 portrait:gap-2 text-xs lg:text-sm xl:text-base portrait:text-xs">
              {['隐私政策', '服务条款', '法律声明'].map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleLinkClick(item)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute bottom-0 left-0 w-full h-0.5 lg:h-1 portrait:h-0.5 bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-600" />
    </footer>
  );
};

export default Footer; 